/**
 * ElevenLabs model identifiers — must match Galactus
 * `VoiceTuningSettings.ElevenLabs.Model{V3,FlashV2_5,MultilingualV2}`.
 *
 * Single source of truth. Imported by client UI helpers (voiceSettingsHelpers),
 * server API routes (voices/[id]/test, voices/[id]/sample/[lang]), and the
 * TTS dispatcher (utils/admin/voices.ts).
 */
export const ELEVEN_FLASH_V2_5 = "eleven_flash_v2_5";
export const ELEVEN_V3 = "eleven_v3";
export const ELEVEN_MULTILINGUAL_V2 = "eleven_multilingual_v2";

export type ElevenLabsModel =
   | typeof ELEVEN_FLASH_V2_5
   | typeof ELEVEN_V3
   | typeof ELEVEN_MULTILINGUAL_V2;

/** Set form for runtime allowlist checks (e.g. unknown raw values from request bodies). */
export const ALLOWED_MODELS: ReadonlySet<string> = new Set([
   ELEVEN_FLASH_V2_5,
   ELEVEN_V3,
   ELEVEN_MULTILINGUAL_V2,
]);

export const DEFAULT_ELEVEN_LABS_MODEL: ElevenLabsModel = ELEVEN_FLASH_V2_5;

/** Internal Gemini synthesis choice; the customer configures delivery, not a model. */
export const GEMINI_FLASH_LITE_TTS = "gemini-3.8-flash-lite-tts";
export const GEMINI_FLASH_TTS = "gemini-3.8-flash-tts";
export const DEFAULT_GEMINI_TTS_MODEL = GEMINI_FLASH_LITE_TTS;
export type GeminiTtsModel = typeof GEMINI_FLASH_LITE_TTS | typeof GEMINI_FLASH_TTS;
export const GEMINI_TTS_MODELS: ReadonlySet<string> = new Set([
   GEMINI_FLASH_LITE_TTS,
   GEMINI_FLASH_TTS,
]);

/**
 * Per-model capability matrix. Single source of truth for which tuning
 * fields the UI surfaces and which may appear on the wire — mirrors Galactus
 * `SupportedFieldsByModel`.
 *
 * Stability is supported by every model but rendered differently:
 *  - v3:                 3-state pill (creative / natural / robust)
 *  - multilingual_v2:    0..1 slider
 *  - flash_v2_5:         0..1 slider
 */
export type ModelCaps = {
   stability3: boolean;
   stabilitySlider: boolean;
   speed: boolean;
   similarity: boolean;
   style: boolean;
};

export const MODEL_CAPS: Record<ElevenLabsModel, ModelCaps> = {
   [ELEVEN_V3]: {
      stability3: true,
      stabilitySlider: false,
      speed: false,
      similarity: false,
      style: false,
   },
   [ELEVEN_MULTILINGUAL_V2]: {
      stability3: false,
      stabilitySlider: true,
      speed: true,
      similarity: true,
      style: true,
   },
   [ELEVEN_FLASH_V2_5]: {
      stability3: false,
      stabilitySlider: true,
      speed: true,
      similarity: true,
      style: false,
   },
};

/**
 * Caps for a known model id. `undefined` when the id is omitted or not in
 * `ALLOWED_MODELS` — callers must distinguish those two misses (omitted:
 * keep every field; unrecognized: do not invent support).
 */
export function capsForModel(model: string | null | undefined): ModelCaps | undefined {
   if (!model || !ALLOWED_MODELS.has(model)) return undefined;
   return MODEL_CAPS[model as ElevenLabsModel];
}
